import React, { useEffect, useState } from 'react';
import './produkt.css';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useCart } from '../../CartContext/CartContext';
import CartPopup from '../../components/CartPopup';

// Funktion til at håndtere både markdown og HTML-indhold
const prepareContent = (content) => {
  if (!content) return '';

  // Hvis teksten allerede er HTML (fra rich text editor)
  if (content.includes('<p>') || content.includes('<h') || content.includes('<ul>') || 
      content.includes('<strong>') || content.includes('<u>') || content.includes('<a ')) {
    // Vi beholder HTML som den er, da formatering håndteres korrekt i rich text editor
    return content;
  }

  // Hvis det er markdown eller plain text
  // Konverter overskrifter
  let html = content.replace(/^## (.*?)$/gm, '<h3>$1</h3>');
  html = html.replace(/^# (.*?)$/gm, '<h2>$1</h2>');

  // Konverter fed tekst
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

  // Konverter bullet points
  html = html.replace(/^- (.*?)$/gm, '<li>$1</li>');
  
  // Del teksten på dobbelte linjeskift, hvilket normalt indikerer nye afsnit
  const paragraphs = html.split(/\n\s*\n/); // Split ved tomme linjer
  html = paragraphs.map(p => {
    const trimmedP = p.trim();
    if (trimmedP === '') return '';
    
    // Håndter lister
    if (trimmedP.includes('<li>')) {
      return `<ul>${trimmedP}</ul>`;
    }
    
    // Håndter overskrifter
    if (trimmedP.includes('<h3>') || trimmedP.includes('<h2>')) {
      return trimmedP;
    }
    
    // Almindelige afsnit - bevar enkelte linjeskift som mellemrum
    return `<p>${trimmedP.replace(/\n/g, ' ')}</p>`;
  }).join('\n\n'); // Tilføj afstand mellem afsnit

  return html;
};

// Funktion til at konvertere separate arrays til formateret tekst
const formatProductContent = (product) => {
  if (!product || !product.beskrivelse) return '';

  let formatted = '';
  
  // Først beskrivelsesafsnitene
  if (Array.isArray(product.beskrivelse) && product.beskrivelse.length > 0) {
    formatted += product.beskrivelse.join('\n\n');
  }
  
  // Tilføj specifikationer hvis de findes
  if (Array.isArray(product.features) && product.features.length > 0) {
    formatted += '\n\n## Specifikationer\n';
    formatted += product.features.map(feature => `- ${feature}`).join('\n');
  }
  
  // Tilføj anvendelsesmuligheder hvis de findes
  if (Array.isArray(product.anvendelse) && product.anvendelse.length > 0) {
    formatted += '\n\n## Anvendelse\n';
    formatted += product.anvendelse.map(anv => `- ${anv}`).join('\n');
  }
  
  return formatted;
};

const Produkt = () => {
  const { produktId } = useParams();
  const [produkt, setProdukt] = useState(null);
  const [produkter, setProdukter] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [produktIndhold, setProduktIndhold] = useState('');
  const [quantity, setQuantity] = useState(1);
  const { addToCart, showPopup, lastAddedProduct, lastAddedQuantity, hidePopup } = useCart();

  useEffect(() => {
    const fetchProduktData = async () => {
      try {
        setIsLoading(true);
        // Hent alle produkter
        const produkterDocRef = doc(db, 'content', 'produkter');
        const produkterDoc = await getDoc(produkterDocRef);
        
        if (produkterDoc.exists() && produkterDoc.data().items) {
          const alleProdukter = produkterDoc.data().items;
          setProdukter(alleProdukter);
          
          // Find det specifikke produkt baseret på id
          const valgtProdukt = alleProdukter.find(p => p.id === produktId);
          if (valgtProdukt) {
            setProdukt(valgtProdukt);
            
            // Håndter enten produktBeskrivelse eller konverter de separate arrays
            if (valgtProdukt.produktBeskrivelse) {
              setProduktIndhold(valgtProdukt.produktBeskrivelse);
            } else {
              setProduktIndhold(formatProductContent(valgtProdukt));
            }
          }
        } else {
          // Brug hardcoded data som fallback
          const fallbackProdukter = {
    'foelelseskort': {
      id: 'foelelseskort',
      navn: 'Følelseskort',
      pris: '599 kr.',
      billede: '/media/nyheder/følelseskort.avif',
      beskrivelse: [
        'Følelseskort er et værdifuldt redskab i terapeutisk arbejde med børn og unge. De giver børn mulighed for at udtrykke deres følelser, når ord kan være svære at finde.',
        'Vores følelseskort er udviklet med udgangspunkt i vores mangeårige erfaring med mentaliseringsbaseret terapi. De er designet til at hjælpe børn med at genkende, forstå og sætte ord på deres følelser.',
        'Kortene kan bruges i en række forskellige terapeutiske sammenhænge og aktiviteter, fra individuel terapi til familiesamtaler og gruppesessioner.'
      ],
      features: [
        '32 kort med forskellige følelsesudtryk',
        'Guidebog med forslag til øvelser og spil',
        'Robust materiale der holder til daglig brug',
        'Designet af psykologer med speciale i børn og unge'
      ],
      anvendelse: [
        'Individuel terapi med børn og unge',
        'Familiesamtaler og -terapi',
        'Gruppeterapi',
        'Skoler og institutioner',
        'Hjemmebrug for forældre'
      ]
    },
    'mbt-bogen': {
      id: 'mbt-bogen',
      navn: 'Mentalisering i børnehøjde',
      pris: '299 kr.',
      billede: '/media/hero/MBT-boger.avif',
      beskrivelse: [
        'Mentalisering i børnehøjde er en praktisk guide til hvordan mentaliseringsbaseret terapi kan anvendes i arbejdet med børn.',
        'Bogen er skrevet af psykologerne bag Psykologisk Perspektiv, der har mange års erfaring med mentaliseringsbaseret terapi med børn, unge og familier.',
        'Den er rig på konkrete eksempler, dialoger og praktiske øvelser, der kan bruges direkte i terapeutisk arbejde eller som inspiration for forældre, lærere og andre der arbejder med børn.'
      ],
      features: [
        '220 sider med praktiske eksempler og øvelser',
        'Teorien bag mentalisering forklaret på en letforståelig måde',
        'Konkrete dialogeksempler til inspiration',
        'Illustreret med tegninger, der understøtter forståelsen'
      ],
      anvendelse: [
        'Psykologer og terapeuter',
        'Pædagoger og lærere',
        'Forældre',
        'Studerende inden for psykologi og pædagogik'
      ]
    },
    'folgeskab': {
      id: 'folgeskab',
      navn: 'Følgeskab med barnet',
      pris: '349 kr.',
      billede: '/media/hero/MBT-klodser.avif',
      beskrivelse: [
        'Følgeskab med barnet er en bog om hvordan vi som voksne kan følge barnets følelsesmæssige udvikling og skabe trygge rammer for deres mentalisering.',
        'Bogen er baseret på nyeste forskning inden for udviklingspsykologi og mentaliseringsbaseret terapi, og giver konkrete værktøjer til at støtte barnet i dets følelsesmæssige udvikling.',
        'Med denne bog får du både teoretisk forståelse og praktiske redskaber til at styrke dit følgeskab med barnet i både medgang og modgang.'
      ],
      features: [
        '250 sider med teori og praksis',
        'Refleksionsøvelser til egen praksis',
        'Cases der illustrerer følgeskab i forskellige situationer',
        'Referencer til nyeste forskning inden for området'
      ],
      anvendelse: [
        'Forældre',
        'Psykologer og terapeuter',
        'Pædagoger og lærere',
        'Plejeforældre og familiebehandlere'
      ]
    }
  };

          setProdukter(Object.values(fallbackProdukter));
          if (fallbackProdukter[produktId]) {
            const valgtProdukt = fallbackProdukter[produktId];
            setProdukt(valgtProdukt);
            setProduktIndhold(formatProductContent(valgtProdukt));
          }
        }
      } catch (error) {
        console.error('Fejl ved hentning af produkt data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduktData();
  }, [produktId]);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0) {
      setQuantity(value);
    }
  };

  const handleAddToCart = () => {
    if (produkt) {
      addToCart(produkt, quantity);
    }
  };

  if (isLoading) {
    return <div className="shop-produkt-loading">Indlæser...</div>;
  }

  if (!produkt) {
    return (
      <div className="shop-produkt-side">
        <div className="shop-produkt-container">
          <h1>Produkt ikke fundet</h1>
          <p>Det produkt du søger efter findes ikke i vores shop.</p>
          <Link to="/shop" className="shop-tilbage-knap">
            Tilbage til shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-produkt-side">
      <div className="shop-produkt-container">
        <div className="shop-produkt-breadcrumb">
          <Link to="/shop">Shop</Link> / {produkt.navn}
        </div>
        
        <div className="shop-produkt-kort">
          <div className="shop-produkt-kort-venstre">
            <img src={produkt.billede} alt={produkt.navn} />
          </div>
          <div className="shop-produkt-kort-højre">
            <h1>{produkt.navn}</h1>
            <p className="shop-produkt-pris">{produkt.pris}</p>
            
            <div className="shop-produkt-indhold" dangerouslySetInnerHTML={{ __html: prepareContent(produktIndhold) }}>
            </div>
            
            <div className="shop-produkt-bestilling">
              <h2>Bestilling</h2>
              
              <div className="shop-produkt-køb">
                <div className="shop-antal-vælger">
                  <label htmlFor="quantity">Antal:</label>
                  <input 
                    type="number" 
                    id="quantity"
                    min="1" 
                    value={quantity}
                    onChange={handleQuantityChange}
                  />
                </div>
                
                <div className="shop-produkt-knapper">
                  <button className="shop-tilfoej-knap" onClick={handleAddToCart}>
                    Tilføj til kurv
                  </button>
                  <Link to="/kurv" className="shop-se-kurv-knap">
                    Se kurv
                  </Link>
                </div>
              </div>
              

            </div>
          </div>
        </div>
        
        <div className="shop-produkt-tilbage">
          <Link to="/shop" className="shop-tilbage-knap">
            Tilbage til shop
          </Link>
        </div>
      </div>
      
      <CartPopup 
        isVisible={showPopup}
        product={lastAddedProduct}
        quantity={lastAddedQuantity}
        onClose={hidePopup}
        onGoToCart={hidePopup}
      />
    </div>
  );
};

export default Produkt;
